import { defineStore } from 'pinia';

import { useManagementApi } from '../api/managementApi';
import type {
  TopicSetCreateRequest,
  TopicSetFindOneResponse,
  TopicSetsFindAllRequest,
  TopicSetsFindAllResponse,
  TopicSetUpdateRequest
} from '../api/managementApi/schema';
import { removeRowByUuid, updateRow } from '../helpers/rows';
import { getStoreName } from '../helpers/utility';

export type DetailTopicSet = TopicSetFindOneResponse;
export type ListingTopicSet = TopicSetsFindAllResponse['rows'][number];

export type TopicSetsState = {
  topicSets: ListingTopicSet[];
};

export const useTopicSetsStore = defineStore(getStoreName('topic-sets'), {
  // We don't persist this as there's no need to & to allow the current selection to differ if the
  // user has different configurations open in different browser tabs.
  persist: false,
  pagination: true,
  state: (): TopicSetsState => ({
    topicSets: []
  }),
  actions: {
    async findAll(reset: boolean, options?: TopicSetsFindAllRequest) {
      await this.$paginate({
        items: this.topicSets,
        type: 'offset',
        limit: 50,
        reset,
        request: async pagination => {
          return (await useManagementApi().topicSets.findAll({ ...pagination, ...options })).data
            ?.rows;
        }
      });
    },
    async findOne(uuid: string) {
      return (await useManagementApi().topicSet.findOne(uuid)).data;
    },
    async create(create: TopicSetCreateRequest) {
      return await useManagementApi().topicSet.create(create);
    },
    async update(update: TopicSetUpdateRequest) {
      const result = await useManagementApi().topicSet.update(update);

      if (result.errored) {
        return false;
      }

      updateRow(this.topicSets, update);

      return true;
    },
    async remove(uuid: string) {
      const result = await useManagementApi().topicSet.remove({ uuid });

      if (result.errored) {
        return false;
      }

      removeRowByUuid(this.topicSets, uuid);
      return true;
    }
  }
});
